import React, { useState } from "react";
import { Share2, Copy, Check, Mail, MessageSquare, Gift } from "lucide-react";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

// Refer-a-Friend share widget shown on the order-success page.
// Encourages new clients to share Knight Credit Consultants with friends.
export const ReferAFriend = ({ referrerName = "" }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://knightcreditconsultants.com";
  const message = `I just started repairing my credit with Knight Credit Consultants and the process has been amazing. If your credit needs a boost, check them out:`;
  const fullText = `${message} ${shareUrl}`;

  const encMsg = encodeURIComponent(message);
  const encUrl = encodeURIComponent(shareUrl);
  const encFull = encodeURIComponent(fullText);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast({ title: "Link copied!", description: "Share it with a friend who needs better credit." });
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast({ title: "Couldn't copy", description: "Please copy the link manually.", variant: "destructive" });
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Knight Credit Consultants", text: message, url: shareUrl });
      } catch (_) { /* user cancelled */ }
    } else {
      handleCopy();
    }
  };

  const shareLinks = [
    {
      key: "sms",
      label: "Text",
      icon: MessageSquare,
      href: `sms:?&body=${encFull}`,
      className: "bg-emerald-500 hover:bg-emerald-600 text-white",
    },
    {
      key: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent("You need to see this — Knight Credit Consultants")}&body=${encFull}`,
      className: "bg-knight-navy hover:bg-knight-navy-dark text-white",
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: null,
      href: `https://wa.me/?text=${encFull}`,
      className: "bg-[#25D366] hover:bg-[#1ebe57] text-white",
      svg: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
        </svg>
      ),
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: null,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encMsg}`,
      className: "bg-[#1877F2] hover:bg-[#0e63d6] text-white",
      svg: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      key: "x",
      label: "X",
      icon: null,
      href: `https://twitter.com/intent/tweet?text=${encMsg}&url=${encUrl}`,
      className: "bg-black hover:bg-neutral-800 text-white",
      svg: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
  ];

  return (
    <div
      className="relative mt-12 rounded-3xl overflow-hidden border border-knight-gold/30 knight-gradient text-white text-left shadow-xl"
      data-testid="refer-a-friend-widget"
    >
      <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-knight-gold/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative p-7 sm:p-9">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-knight-gold text-knight-navy flex items-center justify-center flex-shrink-0 shadow-lg">
            <Gift size={24} />
          </div>
          <div>
            <p className="text-knight-gold font-semibold tracking-[0.2em] text-[11px]">SHARE THE WIN</p>
            <h2 className="font-display text-2xl font-bold leading-tight">Refer a friend</h2>
          </div>
        </div>
        <p className="text-white/80 text-sm leading-relaxed max-w-md mb-6">
          Know someone whose credit could use a knight in shining armor? Send them your link &mdash; better credit changes lives.
        </p>

        {/* Copyable link bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <div className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 font-mono text-sm text-white/90 truncate flex items-center backdrop-blur" data-testid="referral-link-text">
            {shareUrl}
          </div>
          <Button
            type="button"
            onClick={handleCopy}
            data-testid="copy-referral-link-btn"
            className="bg-knight-gold hover:bg-amber-400 text-knight-navy font-bold whitespace-nowrap btn-lift"
          >
            {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
            {copied ? "Copied!" : "Copy Link"}
          </Button>
        </div>

        {/* Share buttons */}
        <div className="flex flex-wrap gap-2.5">
          {shareLinks.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.key}
                href={s.href}
                target={s.key === "sms" || s.key === "email" ? undefined : "_blank"}
                rel="noopener noreferrer"
                data-testid={`share-${s.key}-btn`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors btn-lift ${s.className}`}
              >
                {Icon ? <Icon size={16} /> : s.svg}
                <span>{s.label}</span>
              </a>
            );
          })}
          <button
            type="button"
            onClick={handleNativeShare}
            data-testid="share-more-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white/15 hover:bg-white/25 text-white border border-white/20 transition-colors btn-lift"
          >
            <Share2 size={16} /> More
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferAFriend;
